/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import InsuranceStepIndicator from 'components/atoms/insurance_step_indicator';
import AiAutofillNotice, {
  IaBadge,
  AutofillValidationHint,
} from 'components/atoms/ai_autofill_notice';
import AiSourceAlertBox from 'components/atoms/ai_source_alert_box';
import ClientPolicyholdersModal from 'components/atoms/client_policyholders_modal';
import QuoteGuaranteeHeader from 'components/atoms/quote_guarantee_header';
import LegalBondNotice from 'components/atoms/legal_bond_notice';
import useQuoteFlow from 'hooks/useQuoteFlow';
import useInsuredCnpjAiAlert from 'hooks/useInsuredCnpjAiAlert';
import { sanitizeCnpj, applyMask, hasAutofilledField } from 'services/conlicitaseg';
import { looksLikeCnpj, validateCnpj, shouldShowCnpjError } from 'utils/cnpj';
import { ApplicationState } from 'store';
import {
  clearCompanyName,
  clearFormQuote,
  getClientPolicyholders,
  getCompanyName,
  getInsured,
  searchCompanyData,
  setFormQuote,
} from 'store/modules/conlicitaseg_store/actions';
import { ConlicitasegState, InsuredData } from 'store/modules/conlicitaseg_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

const CNPJ_MASK = '##.###.###/####-##';
const ZIP_CODE_MASK = '#####-###';

const INSURED_FIELDS = [
  'insured_name',
  'insured_zip_code',
  'insured_address',
  'insured_number',
  'insured_district',
  'insured_city',
  'insured_state',
] as const;

type InsuredField = typeof INSURED_FIELDS[number];

// A API devolve 'null' como texto quando não encontra o órgão
const isBlank = (value?: string | null) => !value || value === 'null' || value.trim() === '';

const maskZipCode = (value?: string) => (value ? applyMask(value, ZIP_CODE_MASK) : '');

const insuredValuesFrom = (data: InsuredData): Record<InsuredField, string> => ({
  insured_name: data.insured_name || '',
  insured_zip_code: maskZipCode(data.insured_zip_code),
  insured_address: data.insured_address || '',
  insured_number: data.insured_number || '',
  insured_district: data.insured_district || '',
  insured_city: data.insured_city || '',
  insured_state: data.insured_state || '',
});

const EMPTY_INSURED: Record<InsuredField, string> = {
  insured_name: '',
  insured_zip_code: '',
  insured_address: '',
  insured_number: '',
  insured_district: '',
  insured_city: '',
  insured_state: '',
};

const QuoteHolderData: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    formProcess,
    insured_data,
    request,
    get_company_name,
    autofilled_fields,
    client_policyholders,
    contract_uploaded,
  } = useSelector<ApplicationState, ConlicitasegState>((state) => state.session.conlicitaseg);
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { config, isExecucao, pathFor, previousPathFor, stepIndicatorProps } = useQuoteFlow();
  const { showAlert: showInsuredCnpjAiAlert, dismissAlert: dismissInsuredCnpjAiAlert } =
    useInsuredCnpjAiAlert(autofilled_fields);

  const company = profile.company_data?.data;
  const profileCnpj = sanitizeCnpj(company?.cnpj || '');

  const [form, setForm] = useState({
    policyholder_cnpj: formProcess.policyholder_cnpj
      ? applyMask(formProcess.policyholder_cnpj, CNPJ_MASK)
      : '',
    policyholder_company_name: formProcess.policyholder_company_name || '',
    policyholder_cnpj_matriz: formProcess.policyholder_cnpj_matriz || '',
    insured_cnpj: formProcess.insured_cnpj ? applyMask(formProcess.insured_cnpj, CNPJ_MASK) : '',
    insured_name: isBlank(formProcess.insured_name) ? '' : formProcess.insured_name,
    insured_zip_code: maskZipCode(formProcess.insured_zip_code),
    insured_address: formProcess.insured_address || '',
    insured_number: formProcess.insured_number || '',
    insured_district: formProcess.insured_district || '',
    insured_city: formProcess.insured_city || '',
    insured_state: formProcess.insured_state || '',
  });

  const [policyholderCnpjTouched, setPolicyholderCnpjTouched] = useState(false);
  const [insuredCnpjTouched, setInsuredCnpjTouched] = useState(false);
  const [insuredResultConfirmed, setInsuredResultConfirmed] = useState(
    !isBlank(formProcess.insured_name),
  );
  const [showInsuredResult, setShowInsuredResult] = useState(false);
  const [insuredNotFound, setInsuredNotFound] = useState(false);
  const [showPolicyholdersModal, setShowPolicyholdersModal] = useState(false);

  // Busca do segurado em andamento: só aplica a resposta depois de ver o loading desta busca,
  // para não reaproveitar um insured_data antigo que ainda esteja no store
  const insuredSearch = useRef<'auto' | 'manual' | null>(null);
  const insuredSearchStarted = useRef(false);
  const autoSearchedInsured = useRef(false);

  const policyholderCnpj = sanitizeCnpj(form.policyholder_cnpj);
  const isPolicyholderCnpjValid = validateCnpj(form.policyholder_cnpj);
  const isInsuredCnpjValid = validateCnpj(form.insured_cnpj);
  const showPolicyholderCnpjError = shouldShowCnpjError(
    form.policyholder_cnpj,
    policyholderCnpjTouched,
  );
  const showInsuredCnpjError = shouldShowCnpjError(form.insured_cnpj, insuredCnpjTouched);

  const companyNameRequest = request.get_company_name;
  const companyNameFound = companyNameRequest.loaded && get_company_name.name.trim() !== '';
  const companyNameMissing =
    companyNameRequest.error || (companyNameRequest.loaded && !companyNameFound);
  const hasCompanyName = form.policyholder_company_name.trim() !== '';

  const isLinkedCnpj =
    (!!profileCnpj && policyholderCnpj === profileCnpj) ||
    (client_policyholders || []).includes(policyholderCnpj);
  const linkNoticeEnabled = config.showLinkedCnpjNotice && !contract_uploaded;
  const showPolicyholderLinkNotice =
    linkNoticeEnabled &&
    hasCompanyName &&
    policyholderCnpj.length === 14 &&
    !!request.get_client_policyholders?.loaded;

  const showAutofillMark = hasAutofilledField(autofilled_fields, form);

  const isInsuredFilled =
    INSURED_FIELDS.every((field) => !isBlank(form[field])) && isInsuredCnpjValid;
  const isPolicyholderFilled = policyholderCnpj !== '' && isPolicyholderCnpjValid && hasCompanyName;
  const canContinue = isPolicyholderFilled && isInsuredFilled;

  const canSearchCompany =
    !companyNameRequest.loading && policyholderCnpj.length === 14 && isPolicyholderCnpjValid;
  const canSearchInsured =
    !request.get_insured.loading &&
    looksLikeCnpj(form.insured_cnpj.trim()) &&
    sanitizeCnpj(form.insured_cnpj).length === 14 &&
    isInsuredCnpjValid;

  const previousPath = previousPathFor('tomador');

  // R3: só o CNPJ do segurado é editável; os demais campos vêm da busca e ficam travados,
  // liberando a digitação quando a busca não encontra o órgão ou falha
  const isInsuredFieldLocked = (field: InsuredField) =>
    !isInsuredCnpjValid ||
    request.get_insured.loading ||
    (!request.get_insured.loaded && !request.get_insured.error) ||
    (insuredResultConfirmed && !isBlank(form[field]));

  useEffect(() => {
    if (linkNoticeEnabled) {
      const policyholdersRequest = request.get_client_policyholders;
      if (!policyholdersRequest?.loading && !policyholdersRequest?.loaded) {
        dispatch(getClientPolicyholders());
      }
    }

    // R1/R2: o CNPJ do tomador (do formulário ou do perfil) é buscado na Receita ao abrir a tela
    const prefillCnpj = config.prefillPolicyholderFromProfile ? profileCnpj : '';
    const cnpj = policyholderCnpj || prefillCnpj;
    if (!validateCnpj(cnpj) || companyNameRequest.loaded || companyNameRequest.loading) return;

    if (!policyholderCnpj) {
      setForm((prev) => ({ ...prev, policyholder_cnpj: applyMask(cnpj, CNPJ_MASK) }));
    }
    dispatch(getCompanyName(cnpj));
  }, []);

  useEffect(() => {
    if (!companyNameRequest.loaded && !companyNameRequest.error) return;

    if (companyNameFound) {
      setForm((prev) => ({
        ...prev,
        policyholder_company_name: get_company_name.name.trim(),
        policyholder_cnpj_matriz: get_company_name.policyholder_cnpj_matriz || '',
      }));
      return;
    }

    // Receita sem nome para o CNPJ da própria empresa: usa a razão social do perfil
    if (policyholderCnpj === profileCnpj && company?.razao_social) {
      setForm((prev) => ({
        ...prev,
        policyholder_company_name: prev.policyholder_company_name || company.razao_social,
      }));
    }
  }, [companyNameRequest.loaded, companyNameRequest.error, get_company_name.name]);

  // Licitação e Fidelize trazem o CNPJ do órgão; a busca completa os dados que faltam
  useEffect(() => {
    if (autoSearchedInsured.current) return;

    const cnpj = sanitizeCnpj(formProcess.insured_cnpj || '');
    if (cnpj.length !== 14 || !validateCnpj(cnpj)) return;
    if (INSURED_FIELDS.every((field) => !isBlank(form[field]))) return;

    autoSearchedInsured.current = true;
    insuredSearch.current = 'auto';
    insuredSearchStarted.current = false;
    dispatch(getInsured(cnpj));
  }, [formProcess.insured_cnpj]);

  useEffect(() => {
    if (request.get_insured.loading && insuredSearch.current) {
      insuredSearchStarted.current = true;
    }
  }, [request.get_insured.loading]);

  useEffect(() => {
    const { loaded, error } = request.get_insured;
    const mode = insuredSearch.current;
    if (!mode || !insuredSearchStarted.current || (!loaded && !error)) return;

    insuredSearch.current = null;
    insuredSearchStarted.current = false;

    const found = loaded && !!insured_data && !isBlank(insured_data.insured_name);
    setInsuredNotFound(!found);
    if (!found) return;

    if (mode === 'manual') {
      setShowInsuredResult(true);
      return;
    }

    const fetched = insuredValuesFrom(insured_data);
    setForm((prev) => {
      const filled = INSURED_FIELDS.reduce(
        (values, field) => ({
          ...values,
          [field]: isBlank(prev[field]) ? fetched[field] : prev[field],
        }),
        {} as Record<InsuredField, string>,
      );
      return {
        ...prev,
        ...filled,
        insured_cnpj: applyMask(sanitizeCnpj(formProcess.insured_cnpj || ''), CNPJ_MASK),
      };
    });
    setInsuredResultConfirmed(true);
  }, [request.get_insured.loaded, request.get_insured.error, insured_data]);

  const handleSearchCompany = () => {
    if (!canSearchCompany) return;
    dispatch(getCompanyName(policyholderCnpj));
  };

  const handleSearchInsured = () => {
    if (!canSearchInsured) return;

    setForm((prev) => ({ ...prev, ...EMPTY_INSURED }));
    setInsuredResultConfirmed(false);
    setShowInsuredResult(false);
    setInsuredNotFound(false);
    insuredSearch.current = 'manual';
    insuredSearchStarted.current = false;
    dispatch(getInsured(sanitizeCnpj(form.insured_cnpj)));
  };

  const handleSelectInsuredResult = () => {
    if (!insured_data) return;

    setForm((prev) => ({ ...prev, ...insuredValuesFrom(insured_data) }));
    setShowInsuredResult(false);
    setInsuredResultConfirmed(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, search: () => void) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    search();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'policyholder_cnpj') {
      setPolicyholderCnpjTouched(false);
      if (companyNameRequest.loaded || companyNameRequest.error) {
        dispatch(clearCompanyName());
        setForm({
          ...form,
          policyholder_cnpj: applyMask(value, CNPJ_MASK),
          policyholder_company_name: '',
          policyholder_cnpj_matriz: '',
        });
      } else {
        setForm({ ...form, policyholder_cnpj: applyMask(value, CNPJ_MASK) });
      }
    } else if (name === 'insured_cnpj') {
      dismissInsuredCnpjAiAlert();
      setInsuredCnpjTouched(false);
      setShowInsuredResult(false);
      setInsuredNotFound(false);
      setForm({ ...form, insured_cnpj: applyMask(value, CNPJ_MASK) });
    } else if (name === 'insured_zip_code') {
      setForm({ ...form, insured_zip_code: applyMask(value, ZIP_CODE_MASK) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleCancel = () => {
    dispatch(clearFormQuote());
    history.push('/conlicitaseg');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;

    const matriz = sanitizeCnpj(form.policyholder_cnpj_matriz);
    dispatch(
      setFormQuote({
        ...form,
        policyholder_cnpj: policyholderCnpj,
        policyholder_cnpj_matriz: matriz,
        insured_cnpj: sanitizeCnpj(form.insured_cnpj),
      }),
    );
    dispatch(searchCompanyData(policyholderCnpj, matriz));
    history.push(pathFor('licitacao'));
  };

  const renderErrorBox = (message: string, detail?: string) => (
    <div className="mt-2 p-3 border border-danger rounded bg-light text-left w-100">
      <div className="d-flex align-items-center">
        <i className="material-icons text-danger mr-2" aria-hidden="true">
          error_outline
        </i>
        <span className="text-danger font-weight-bold">{message}</span>
      </div>
      {detail && <small className="text-muted d-block mt-1">{detail}</small>}
    </div>
  );

  const renderSearchButton = (onClick: () => void, disabled: boolean, loading: boolean) => (
    <div className="input-group-append">
      <button
        type="button"
        className="btn btn-outline-primary search-button"
        onClick={onClick}
        disabled={disabled}
        aria-label="Buscar CNPJ"
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          <i className="material-icons" aria-hidden="true">
            search
          </i>
        )}
      </button>
    </div>
  );

  const renderInsuredInput = (field: InsuredField, label: string, colClass: string) => (
    <div className={`${colClass} mb-3`}>
      <label htmlFor={field}>{label}</label>
      <input
        id={field}
        name={field}
        className="form-control"
        value={form[field]}
        onChange={handleChange}
        disabled={isInsuredFieldLocked(field)}
      />
    </div>
  );

  return (
    <Container>
      <div className="page-container">
        <div className="container-fluid big-resolutions py-5 px-md-5">
          <div className="row justify-content-center mt-5">
            <div className="col-12 mx-auto">
              <div className="row align-items-stretch">
                <div className="col-md-9">
                  {config.participationHeader && !isExecucao && (
                    <QuoteGuaranteeHeader title="Seguro Garantia de Participação" />
                  )}
                  <div className="shadow-sm p-4 bg-white rounded">
                    <h3 className="mb-2">{config.holderTitle}</h3>
                    {showAutofillMark && <AiAutofillNotice />}
                    <form onSubmit={handleSubmit} autoComplete="off">
                      <div className="mb-4">
                        <h5 className="mt-4">
                          {isExecucao
                            ? 'Dados do Tomador (vencedor da licitação)'
                            : 'Dados do Tomador (participante da licitação)'}
                          {showAutofillMark && <IaBadge />}
                        </h5>
                        {showAutofillMark && <AutofillValidationHint />}
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="policyholder_cnpj">CNPJ</label>
                            <div className="input-group">
                              <input
                                id="policyholder_cnpj"
                                name="policyholder_cnpj"
                                className={`form-control ${
                                  showPolicyholderCnpjError ? 'is-invalid' : ''
                                }`}
                                value={form.policyholder_cnpj}
                                onChange={handleChange}
                                onBlur={() => setPolicyholderCnpjTouched(true)}
                                onKeyDown={(e) => handleKeyDown(e, handleSearchCompany)}
                              />
                              {renderSearchButton(
                                handleSearchCompany,
                                !canSearchCompany,
                                companyNameRequest.loading,
                              )}
                            </div>
                            {showPolicyholderCnpjError &&
                              renderErrorBox('CNPJ inválido, confira e tente novamente')}
                            {get_company_name.policyholder_public_legal_nature && (
                              <div className="mt-2 p-3 border border-warning rounded bg-light text-left w-100">
                                <div className="d-flex align-items-center">
                                  <i
                                    className="material-icons text-warning mr-2"
                                    aria-hidden="true"
                                  >
                                    warning
                                  </i>
                                  <span className="text-warning font-weight-bold">
                                    O CNPJ digitado é de natureza publica, verifique novamente antes
                                    de continuar
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="policyholder_company_name">Razão Social</label>
                            <input
                              id="policyholder_company_name"
                              name="policyholder_company_name"
                              className="form-control"
                              value={form.policyholder_company_name}
                              onChange={handleChange}
                              disabled={
                                !isPolicyholderCnpjValid ||
                                companyNameRequest.loading ||
                                !companyNameMissing
                              }
                            />
                            {companyNameMissing &&
                              !hasCompanyName &&
                              renderErrorBox('Digite a razão social do tomador.')}
                          </div>
                        </div>
                        {showPolicyholderLinkNotice &&
                          (isLinkedCnpj ? (
                            <div className="d-flex align-items-center">
                              <i
                                className="material-icons text-success mr-2"
                                style={{ fontSize: 18 }}
                                aria-hidden="true"
                              >
                                check_circle
                              </i>
                              <span>
                                CNPJ vinculado a sua empresa.{' '}
                                <button
                                  type="button"
                                  className="btn btn-link p-0 align-baseline"
                                  onClick={() => setShowPolicyholdersModal(true)}
                                >
                                  Clique aqui
                                </button>{' '}
                                para ver seus CNPJs.
                              </span>
                            </div>
                          ) : (
                            <div>
                              Este CNPJ não está vinculado sua empresa, gostaria de adicioná-lo para
                              futuras cotações e emissões de seguro garantia?{' '}
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline"
                                onClick={() => setShowPolicyholdersModal(true)}
                              >
                                Clique aqui para adicionar.
                              </button>
                            </div>
                          ))}
                      </div>

                      <div className="mb-4">
                        <h5 className="mt-4">
                          Dados do Segurado (órgão público)
                          {showAutofillMark && <IaBadge />}
                        </h5>
                        <div className="mb-3">
                          Essa informação pode ser encontrada no edital, termo de referência, e-mail
                          do órgão ou contrato da licitação.
                        </div>
                        <AiSourceAlertBox
                          id="insured-cnpj-ai-alert"
                          visible={showInsuredCnpjAiAlert}
                        >
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label htmlFor="insured_cnpj">CNPJ</label>
                              <div className="input-group">
                                <input
                                  id="insured_cnpj"
                                  name="insured_cnpj"
                                  className={`form-control ${
                                    showInsuredCnpjError ? 'is-invalid' : ''
                                  }`}
                                  value={form.insured_cnpj}
                                  onChange={handleChange}
                                  onBlur={() => setInsuredCnpjTouched(true)}
                                  onKeyDown={(e) => handleKeyDown(e, handleSearchInsured)}
                                />
                                {renderSearchButton(
                                  handleSearchInsured,
                                  !canSearchInsured,
                                  request.get_insured.loading,
                                )}
                              </div>
                              {showInsuredCnpjError &&
                                renderErrorBox('CNPJ inválido, confira e tente novamente')}
                              {showInsuredResult && insured_data && (
                                <button
                                  type="button"
                                  onClick={handleSelectInsuredResult}
                                  className="mt-2 p-3 border rounded bg-light insured-search-result text-left w-100"
                                >
                                  <small className="text-muted d-block mb-1">
                                    Clique para confirmar e preencher dados:
                                  </small>
                                  <div className="font-weight-bold text-primary">
                                    {form.insured_cnpj} - {insured_data.insured_name}
                                  </div>
                                </button>
                              )}
                              {insuredNotFound &&
                                !request.get_insured.error &&
                                renderErrorBox(
                                  'Verifique o CNPJ digitado',
                                  'O CNPJ informado não foi encontrado. Certifique-se de que digitou corretamente.',
                                )}
                            </div>
                            {renderInsuredInput('insured_name', 'Razão Social', 'col-md-6')}
                          </div>
                        </AiSourceAlertBox>
                        {request.get_insured.error &&
                          renderErrorBox('Digite a razão social e os dados do segurado.')}
                        <div className="row">
                          {renderInsuredInput('insured_zip_code', 'CEP', 'col-md-2')}
                          {renderInsuredInput('insured_address', 'Endereço', 'col-md-8')}
                          {renderInsuredInput('insured_number', 'Número', 'col-md-2')}
                          {renderInsuredInput('insured_district', 'Bairro', 'col-md-4')}
                          {renderInsuredInput('insured_city', 'Cidade', 'col-md-4')}
                          {renderInsuredInput('insured_state', 'Estado', 'col-md-4')}
                        </div>
                      </div>

                      <LegalBondNotice />

                      <div className="d-flex justify-content-end align-items-center mt-4">
                        {previousPath && (
                          <button
                            type="button"
                            className="btn btn-link mr-2"
                            onClick={() => history.push(previousPath)}
                          >
                            Voltar etapa
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleCancel}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary ml-2"
                          disabled={!canContinue}
                        >
                          Continuar
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="col-md-3 d-none d-md-block">
                  <InsuranceStepIndicator {...stepIndicatorProps('tomador')} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClientPolicyholdersModal
        show={showPolicyholdersModal}
        onHide={() => setShowPolicyholdersModal(false)}
        cnpj={policyholderCnpj}
        companyName={form.policyholder_company_name}
        isLinked={isLinkedCnpj}
      />
    </Container>
  );
};

export default QuoteHolderData;
